import React, { useState, useEffect } from "react";
import { drawings } from "../../data/drawings";
import "./DrawingsGallery.css";

/** Returns the thumbnail (WebP) path for a given drawing file path. */
const thumbPath = (file) => {
  const name = file.replace(/^.*\//, "").replace(/\.(jpe?g|png|webp)$/i, "");
  return `/drawings/thumbs/${name}.webp`;
};

/** Fades in once the image has loaded. */
const LazyImage = ({ src, alt, className }) => {
  const [loaded, setLoaded] = useState(false);
  return (
    <img
      className={`${className} ${loaded ? "img-loaded" : "img-loading"}`}
      src={src}
      alt={alt}
      loading="lazy"
      decoding="async"
      onLoad={() => setLoaded(true)}
    />
  );
};

const DrawingsGallery = () => {
  const [selectedIndex, setSelectedIndex] = useState(null);

  useEffect(() => {
    if (selectedIndex === null) return;

    const handleKeyDown = (e) => {
      if (e.key === "ArrowRight") {
        setSelectedIndex((prev) => (prev + 1) % drawings.length);
      } else if (e.key === "ArrowLeft") {
        setSelectedIndex(
          (prev) => (prev - 1 + drawings.length) % drawings.length,
        );
      } else if (e.key === "Escape") {
        setSelectedIndex(null);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedIndex]);

  return (
    <div className="drawings-gallery-container">
      <div className="drawings-gallery-header">
        <h2 className="drawings-gallery-title">🎨 Drawings Gallery</h2>
        <p className="drawings-gallery-subtitle">
          {drawings.length} original artworks
        </p>
      </div>

      <div className="drawings-gallery">
        {drawings.map((drawing, index) => (
          <div
            className="drawing-card"
            key={index}
            onClick={() => setSelectedIndex(index)}
            role="button"
            tabIndex={0}
            onKeyPress={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                setSelectedIndex(index);
              }
            }}
          >
            <div className="drawing-card-image-wrapper">
              <LazyImage
                src={thumbPath(drawing.file)}
                alt={drawing.title}
                className="drawing-card-image"
              />
              <div className="drawing-card-overlay">
                <span className="drawing-card-view-icon">🔍</span>
              </div>
            </div>
            <h3 className="drawing-card-title">{drawing.title}</h3>
          </div>
        ))}
      </div>

      {selectedIndex !== null && (
        <div
          className="lightbox"
          onClick={() => setSelectedIndex(null)}
          role="dialog"
          aria-modal="true"
        >
          <button
            className="lightbox-close"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedIndex(null);
            }}
            aria-label="Close lightbox"
          >
            ✕
          </button>
          <button
            className="lightbox-arrow left"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedIndex(
                (prev) => (prev - 1 + drawings.length) % drawings.length,
              );
            }}
            aria-label="Previous image"
          >
            ‹
          </button>
          <div className="lightbox-content">
            <img
              src={drawings[selectedIndex].file}
              alt={drawings[selectedIndex].title}
              className="lightbox-img"
            />
            <div className="lightbox-info">
              <h3 className="lightbox-title">
                {drawings[selectedIndex].title}
              </h3>
              <p className="lightbox-counter">
                {selectedIndex + 1} / {drawings.length}
              </p>
            </div>
          </div>
          <button
            className="lightbox-arrow right"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedIndex((prev) => (prev + 1) % drawings.length);
            }}
            aria-label="Next image"
          >
            ›
          </button>
        </div>
      )}
    </div>
  );
};

export default DrawingsGallery;
